import React, { useCallback, useState, useMemo, useRef } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import axios from "axios"
import moment from "moment"
import clsx from "clsx"
import { useConsultantDetailsContext } from "../ConsultantDetails"
import MyDateDayMonthYearFormField from "@common-components/fields/MyDateDayMonthYearFormField"
import ChartRentabilite from "../../../../../components/kpi/ChartRentabilite"
import ChartIntercontrat from "../../../../../components/kpi/ChartIntercontrat"
import MyAlert from "@common-utils/MyAlert"
import { CONST_API_DASHBOARD_KPI_ENDPOINT } from "../../../../../endpoints"

interface TabItem {
     id: string
     label: string
     icon: string
}

interface ConsultantKpiData {
     turnOverCoutMbp: any[] | null
     intercontrat: any[] | null
}

const ConsultantDetailsIndicateurs = () => {
     const context = useConsultantDetailsContext()
     const consultant_id = context.detailsQuery.data?.consultant!.id!

     // Référence pour le défilement automatique
     const chartsRef = useRef<HTMLDivElement>(null)

     // États de workflow
     const [showForm, setShowForm] = useState<boolean>(true)
     const [appliedValues, setAppliedValues] = useState<{ dateDebut: string; dateFin: string } | null>(null)
     const [loading, setLoading] = useState<boolean>(false)
     const [error, setError] = useState<string | null>(null)
     const [activeTab, setActiveTab] = useState<string>("RENTABILITE")

     const [chartData, setChartData] = useState<ConsultantKpiData>({
          turnOverCoutMbp: null,
          intercontrat: null,
     })

     const validationSchema = Yup.object().shape({
          dateDebut: Yup.string().required("Date de début requise"),
          dateFin: Yup.string().required("Date de fin requise"),
     })

     const fetchConsultantKpi = useCallback(
          async (values: { dateDebut: string; dateFin: string }) => {
               setLoading(true)
               setError(null)

               try {
                    const response = await axios.post(CONST_API_DASHBOARD_KPI_ENDPOINT, {
                         dateDebut: values.dateDebut,
                         dateFin: values.dateFin,
                         consultants: [consultant_id],
                         indicators: ["RENTABILITE", "INTERCONTRATS"],
                    })

                    setChartData(response.data)
                    setAppliedValues(values)
                    setShowForm(false)

                    // Sélection automatique du premier onglet disponible
                    if (response.data.turnOverCoutMbp) setActiveTab("RENTABILITE")
                    else if (response.data.intercontrat) setActiveTab("INTERCONTRATS")

                    // Scroll vers la section des graphiques
                    setTimeout(() => {
                         chartsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }, 100)
               } catch (err: any) {
                    console.error("Erreur KPI Consultant:", err)
                    setError("Impossible de récupérer les indicateurs pour ce consultant.")
               } finally {
                    setLoading(false)
               }
          },
          [consultant_id]
     )

     const formik = useFormik({
          initialValues: {
               dateDebut: appliedValues?.dateDebut || moment().startOf("year").format("YYYY-MM-DD"),
               dateFin: appliedValues?.dateFin || moment().endOf("year").format("YYYY-MM-DD"),
          },
          enableReinitialize: true,
          validationSchema,
          onSubmit: values => {
               fetchConsultantKpi(values)
          },
     })

     const availableTabs = useMemo(() => {
          const tabs: TabItem[] = []
          if (chartData.turnOverCoutMbp) tabs.push({ id: "RENTABILITE", label: "Rentabilité", icon: "fa-chart-pie" })
          if (chartData.intercontrat) tabs.push({ id: "INTERCONTRATS", label: "Intercontrats", icon: "fa-clock" })
          return tabs
     }, [chartData])

     return (
          <div className="d-flex flex-column gap-7">
               {/* MODE ÉDITION (FORMULAIRE) */}
               {showForm ? (
                    <div className="card shadow-sm border border-primary border-dashed bg-light-primary">
                         <div className="card-body p-7">
                              <form onSubmit={formik.handleSubmit}>
                                   <div className="row g-7 align-items-end">
                                        <div className="col-12 col-md-4">
                                             <MyDateDayMonthYearFormField
                                                  placeholder={"Date de début"}
                                                  label="Date Début"
                                                  name="dateDebut"
                                                  formikProps={formik as any}
                                                  classNameLayout="mb-0"
                                             />
                                        </div>
                                        <div className="col-12 col-md-4">
                                             <MyDateDayMonthYearFormField
                                                  placeholder={"Date de fin"}
                                                  label="Date Fin"
                                                  name="dateFin"
                                                  formikProps={formik as any}
                                                  classNameLayout="mb-0"
                                             />
                                        </div>
                                        <div className="col-12 col-md-4 d-flex gap-3">
                                             <button type="submit" className="btn btn-primary flex-grow-1 h-45px" disabled={loading || !formik.isValid}>
                                                  {loading ? (
                                                       <span className="spinner-border spinner-border-sm"></span>
                                                  ) : (
                                                       <>
                                                            <i className="fas fa-search me-2"></i> Générer
                                                       </>
                                                  )}
                                             </button>
                                             {appliedValues && (
                                                  <button type="button" className="btn btn-light h-45px" onClick={() => setShowForm(false)}>
                                                       Annuler
                                                  </button>
                                             )}
                                        </div>
                                   </div>
                              </form>
                         </div>
                    </div>
               ) : (
                    /* MODE VUE (RÉCAPITULATIF) */
                    <div className="bg-light-info p-5 rounded border border-info border-dashed d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-5">
                         <div className="d-flex flex-wrap gap-5 align-items-center">
                              <div className="d-flex flex-column">
                                   <span className="text-muted fs-8 fw-bold text-uppercase">Période</span>
                                   <span className="fw-bolder text-gray-800">
                                        Du {moment(appliedValues?.dateDebut).format("DD/MM/YYYY")} au {moment(appliedValues?.dateFin).format("DD/MM/YYYY")}
                                   </span>
                              </div>
                         </div>

                         <button className="btn btn-sm btn-info fw-bold w-100 w-md-auto" onClick={() => setShowForm(true)}>
                              <i className="fas fa-edit me-2"></i> Modifier les filtres
                         </button>
                    </div>
               )}

               {error && <MyAlert type="danger">{error}</MyAlert>}

               {loading && (
                    <div className="text-center py-10">
                         <span className="spinner-border text-primary"></span>
                         <p className="text-muted mt-3">Génération des indicateurs...</p>
                    </div>
               )}

               {/* AFFICHAGE DES RÉSULTATS (Tant qu'il y a des données, on les affiche, même en mode édition) */}
               {!loading && appliedValues && (
                    <div className="d-flex flex-column gap-5" ref={chartsRef}>
                         {/* NAVIGATION DES ONGLETS */}
                         {availableTabs.length > 1 && (
                              <div className="bg-light rounded p-1 d-flex mb-2">
                                   {availableTabs.map(tab => (
                                        <button
                                             key={tab.id}
                                             onClick={() => setActiveTab(tab.id)}
                                             className={clsx(
                                                  "btn btn-sm px-4 py-3 fw-bold flex-grow-1 transition-all d-flex align-items-center justify-content-center gap-2",
                                                  activeTab === tab.id ? "bg-white shadow-sm text-primary" : "text-gray-600"
                                             )}
                                        >
                                             <i className={clsx("fas fs-7", tab.icon, activeTab === tab.id ? "text-primary" : "text-gray-400")}></i>
                                             {tab.label}
                                        </button>
                                   ))}
                              </div>
                         )}

                         {/* GRAPHIQUES */}
                         <div className="row g-7">
                              {chartData.turnOverCoutMbp && activeTab === "RENTABILITE" && (
                                   <div className="col-12">
                                        <div className="card card-flush shadow-sm p-6">
                                             <ChartRentabilite initialData={chartData.turnOverCoutMbp} />
                                        </div>
                                   </div>
                              )}
                              {chartData.intercontrat && activeTab === "INTERCONTRATS" && (
                                   <div className="col-12">
                                        <div className="card card-flush shadow-sm p-6">
                                             <ChartIntercontrat initialData={chartData.intercontrat} />
                                        </div>
                                   </div>
                              )}
                         </div>
                    </div>
               )}
          </div>
     )
}

export default ConsultantDetailsIndicateurs
